import React, { useEffect, useMemo } from 'react';
import { useHistory, Redirect } from 'react-router';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import useProcessStepController from '../../../model/useProcessStepController';
import {
  OfferService, SpaceService,
} from '../../../support/services/api';
import {
  DeliveryMethod, Address, DeliveryDate, DeliveryShift, PaymentMethod, Summary, Success
} from './FormSteps';
import './styles.css';

import useBasket from '../../../model/useBasket';
import useUser from '../../../model/useUser';
import useOrder from '../../../model/useOrder';

function Checkout() {
  const history = useHistory();
  const userId = useSelector((state) => state.AuthReducer.userId);
  const userData = useSelector((state) => state.AuthReducer.userData);
  const consumerSpaceData = useSelector((state) => state.AuthReducer.producerData);
  const MAXSTEPS = 6;

  const { sharedStates: processStates, commands: processCommands } = useProcessStepController(1); 
  const { sharedStates: { spaceId, basketItems }, commands: basketCommands } = useBasket();
  
  const { sharedStates: order, commands: orderCommands, metaStates: orderMetaStates } = useOrder({
    deliveryAddr: {},
    deliveryAddrId: 0,
    deliveryValue: undefined,
    deliveryDate: undefined,
    deliveryMethod: '',
    deliveryShift: '',
    paymentMethod: '',
    cpf: userData?.cpf,
    donationValue: undefined,
  });

  const { commands: userCommands } = useUser();
  const offersIds = useMemo(() => basketItems.map((basketItem) => basketItem.id), [basketItems]);

  const { data: space = {} } = useQuery(
    ['spaces', { id: spaceId }],
    () => SpaceService().getByIds([spaceId]).then((res) => res.producers[0]),
    {
      keepPreviousData: true,
      enabled: !!spaceId,
    },
  );

  const headerRightText = useMemo(() => (processStates.step === 1 && !basketItems.length ? 'Nada para ver aqui' : `Passo ${processStates.step} de ${MAXSTEPS}`), [processStates.step, basketItems.length, MAXSTEPS]);

  const {
    data: offers,
  } = useQuery(
    ['basketOffers', spaceId, offersIds, basketItems],
    () => OfferService().getByIds(offersIds),
    {
      enabled: !!offersIds && !!basketItems.length,
      select: (res) => res.offers,
      notifyOnChangeProps: 'tracked',
      keepPreviousData: true,
    },
  );
  useEffect(() => {
    if (offers && basketItems.length) {
      orderCommands.changeDataAttributes({
        productsValue: offers?.reduce((acc, cv) => {
          const basketItemAmount = basketItems.find((basketItem) => basketItem.id === cv.id).amount;
          return (acc += cv.value * basketItemAmount);
        }, 0),
        productsCounter: offers?.reduce((acc, cv) => {
          const basketItemAmount = basketItems.find((basketItem) => basketItem.id === cv.id).amount;
          return (acc += basketItemAmount);
        }, 0),
      });
    }
  }, [basketItems, offers]);

  const toCammelCase = {
    grab_and_go: 'grabAndGo',
    home_delivery: 'homeDelivery',
  };

  const availableDeliveryMethods = useMemo(
    () => (space?.delivery ? Object.keys(space.delivery) : []),
    [space],
  );

  const availableDaysOfWeek = useMemo(
    () => space?.delivery?.[toCammelCase[order.deliveryMethod]]?.daysOfWeek,
    [space, order.deliveryMethod],
  );

  const availableGratuityDaysOfWeek = useMemo(
    () => space?.delivery?.homeDelivery?.gratuity?.daysOfWeek,
    [space, order.deliveryMethod],
  );

  const availableShifts = useMemo(
    () => space?.delivery?.[toCammelCase[order.deliveryMethod]]?.shifts,
    [space, order.deliveryMethod],
  );

  useEffect(() => {
    if (!userId || !userData) history.push('/login');
  }, [history, userId, userData]);

  useEffect(() => {
    if (order.productsValue) {
      orderCommands.changeDataAttributes({
        freeFreight: {
          minOrderValue: order.productsValue >= space?.delivery?.homeDelivery?.gratuity?.minOrderValue,
        },
        deliveryValue: order.productsValue >= space?.delivery?.homeDelivery?.gratuity?.minOrderValue ? 0 : undefined,
      });
    }
  }, [order.productsValue]);

  if (!basketItems.length && ![6,7].includes(processStates.step)) return <Redirect to="/basket" />
  
  if(processStates.step === 7 ) return <Success sharedStates={{order, userId, space }}/>;

  return (
    <div className={"checkout-container"}>
      {space && (
        <div className="step-scrollbox">
          {
            {
              1: (basketItems.length > 0) && (
                <DeliveryMethod
                  commands={{ process: processCommands, order: orderCommands}}
                  sharedStates={{
                    process: processStates,
                    order,
                    availableMethods: availableDeliveryMethods,
                    gratuity: space?.delivery?.homeDelivery?.gratuity,
                    headerRightText,
                  }}
                />
              ),
              2: (<DeliveryDate
                commands={{
                  process: processCommands,
                  order: orderCommands,
                }}
                sharedStates={{
                  order,
                  daysOfWeek: availableDaysOfWeek,
                  gratuityDays: availableGratuityDaysOfWeek,
                  gratuity: space?.delivery?.homeDelivery?.gratuity,
                  availableShifts,
                  offers,
                }}

              />),
              3: (<DeliveryShift
                commands={{
                  process: processCommands,
                  order: orderCommands,
                }}
                sharedStates={{
                  order,
                  availableShifts,
                  offers,
                }}
              />),
              4: (<Address
                commands={{
                  process: processCommands, order: orderCommands,
                }}
                sharedStates={{
                  spaceId,
                  space,
                  order,
                  process: processStates,
                  headerRightText,
                  consumerSpaceData,
                  userId,
                  gratuity: space?.delivery?.homeDelivery?.gratuity?.kilometerRadius,
                }}
              />
              ),
              5: (<PaymentMethod
                commands={{
                  process: processCommands,
                  order: orderCommands,
                  user: userCommands,
                }}
                sharedStates={{
                  order,
                  availableMethods: space?.payment?.methods,
                  space,
                  userCPF: userData.cpf,
                  consumerSpaceData,
                }}
              />),
              6: (<Summary
                commands={{
                  process: processCommands,
                  order: orderCommands,
                  user: userCommands,
                  basket: basketCommands,
                }}
                sharedStates={{
                  order,
                  space,
                  user: userData,
                  basketItems,
                  offers,
                  consumerSpaceData,
                  userId,
                  process: processStates,
                  MAXSTEPS,
                }}
                metaStates={orderMetaStates}
              />),
            }[processStates.step]
          }
        </div>
      )}
    </div>
  );
}

export default Checkout;
